import { CalendarClock } from 'lucide-react';
import { PageHeader } from '@/shared/components/ui/PageHeader';
// ─────────────────────────────────────────────────────────────────────────────
// Services & Projects — Timesheets Page
// ─────────────────────────────────────────────────────────────────────────────

import { useMemo, useState, useCallback, useEffect, useRef } from 'react';
import { toast } from 'react-hot-toast';
import {
  Clock, Play, Pause, Square, Plus, Search, Eye, Edit, Trash2,
  Calendar, CheckCircle, XCircle, AlertCircle,
  FileText, Timer, DollarSign, TrendingUp
} from 'lucide-react';
import { Button } from '@/shared/components/ui/Button';
import { Input } from '@/shared/components/ui/Input';
import { Select } from '@/shared/components/ui/Select';
import { Badge } from '@/shared/components/ui/Badge';
import { Card, CardHeader, CardTitle, CardContent } from '@/shared/components/ui/Card';
import { Modal } from '@/shared/components/ui/Modal';
import { Table, TableHeader, TableBody, TableRow, TableHead, TableCell } from '@/shared/components/ui/Table';
import { formatINR } from '@/shared/utils/currency';
import { api } from '@/shared/api/axios';
import { useTimesheets, useRateCards, useProjects } from '../hooks/useServicesProjectsApi';
import { useUserOptions } from '@/shared/hooks/useUserOptions';
import { localToday } from '@/shared/utils/utils';

const TIMESHEET_STATUSES = [
  { value: 'draft', label: 'Draft', color: 'bg-slate-100 text-slate-700' },
  { value: 'submitted', label: 'Submitted', color: 'bg-blue-100 text-blue-800' },
  { value: 'approved', label: 'Approved', color: 'bg-emerald-100 text-emerald-800' },
  { value: 'rejected', label: 'Rejected', color: 'bg-red-100 text-red-800' },
  { value: 'billed', label: 'Billed', color: 'bg-purple-100 text-purple-800' },
];

const ACTIVITY_TAGS = [
  'development', 'design', 'meeting', 'research', 'testing', 'deployment',
  'code_review', 'documentation', 'planning', 'support', 'training', 'other'
];

function errorText(err: unknown, fallback: string) {
  const e = err as { response?: { data?: { error?: string } } };
  return e?.response?.data?.error || fallback;
}

function formatDuration(minutes: number) {
  const h = Math.floor(minutes / 60);
  const m = minutes % 60;
  return h > 0 ? `${h}h ${m}m` : `${m}m`;
}

function getStatusConfig(status: string) {
  return TIMESHEET_STATUSES.find(s => s.value === status) || { label: status, color: 'bg-slate-100 text-slate-700' };
}

export function TimesheetsPage() {
  const [activeTab, setActiveTab] = useState<'timer' | 'entries' | 'weekly' | 'approval'>('timer');
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [projectFilter, setProjectFilter] = useState('');
  const [userFilter, setUserFilter] = useState('');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [creating, setCreating] = useState(false);
  const [editing, setEditing] = useState<any>(null);
  const [viewing, setViewing] = useState<any>(null);
  const [formData, setFormData] = useState<any>({});
  const [saving, setSaving] = useState(false);
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [bulkApproving, setBulkApproving] = useState(false);

  // Timer state
  const [timerRunning, setTimerRunning] = useState(false);
  const [timerPaused, setTimerPaused] = useState(false);
  const [timerSeconds, setTimerSeconds] = useState(0);
  const [timerProjectId, setTimerProjectId] = useState('');
  const [timerTaskId, setTimerTaskId] = useState('');
  const [timerDescription, setTimerDescription] = useState('');
  const [timerBillable, setTimerBillable] = useState(true);
  const timerInterval = useRef<NodeJS.Timeout | null>(null);

  const { data: timesheets = [], isLoading: timesheetsLoading, refetch: refetchTimesheets } = useTimesheets();
  const { data: projects = [] } = useProjects({ status: 'active' });
  const { data: rateCards = [] } = useRateCards({ status: 'active' });
  const { data: users = [] } = useUserOptions();
  const activeRateCard = rateCards[0];

  const [formTasks, setFormTasks] = useState<any[]>([]);
  const [timerTasks, setTimerTasks] = useState<any[]>([]);

  useEffect(() => {
    if (!formData.projectId) {
      setFormTasks([]);
      return;
    }
    const sel = projects.find((p: any) => p._id === formData.projectId || p.id === formData.projectId);
    const embedded = [
      ...(sel?.tasks || []),
      ...(sel?.milestones || []).map((m: any) => ({ _id: m._id, name: m.name })),
    ];
    api.get(`/api/accounts/projects/${formData.projectId}/tasks`)
      .then(res => {
        const fetched = Array.isArray(res.data) ? res.data : [];
        setFormTasks([...embedded, ...fetched]);
      })
      .catch(() => {
        setFormTasks(embedded);
      });
  }, [formData.projectId, projects]);

  useEffect(() => {
    if (!timerProjectId) {
      setTimerTasks([]);
      return;
    }
    const sel = projects.find((p: any) => p._id === timerProjectId || p.id === timerProjectId);
    const embedded = [
      ...(sel?.tasks || []),
      ...(sel?.milestones || []).map((m: any) => ({ _id: m._id, name: m.name })),
    ];
    api.get(`/api/accounts/projects/${timerProjectId}/tasks`)
      .then(res => {
        const fetched = Array.isArray(res.data) ? res.data : [];
        setTimerTasks([...embedded, ...fetched]);
      })
      .catch(() => {
        setTimerTasks(embedded);
      });
  }, [timerProjectId, projects]);

  // Filter timesheets
  const filteredTimesheets = useMemo(() => {
    return timesheets.filter(t => {
      if (search && !t.description?.toLowerCase().includes(search.toLowerCase()) &&
          !t.projectName?.toLowerCase().includes(search.toLowerCase()) &&
          !t.userName?.toLowerCase().includes(search.toLowerCase())) return false;
      if (statusFilter && t.status !== statusFilter) return false;
      if (projectFilter && t.projectId !== projectFilter) return false;
      if (userFilter && t.userId !== userFilter) return false;
      if (dateFrom && t.date && new Date(t.date) < new Date(dateFrom)) return false;
      if (dateTo && t.date && new Date(t.date) > new Date(dateTo)) return false;
      return true;
    });
  }, [timesheets, search, statusFilter, projectFilter, userFilter, dateFrom, dateTo]);

  // Timer logic
  useEffect(() => {
    if (timerRunning && !timerPaused) {
      timerInterval.current = setInterval(() => {
        setTimerSeconds(s => s + 1);
      }, 1000);
    } else {
      if (timerInterval.current) clearInterval(timerInterval.current);
    }
    return () => { if (timerInterval.current) clearInterval(timerInterval.current); };
  }, [timerRunning, timerPaused]);

  const resetForm = useCallback(() => {
    const today = localToday();
    const activeProject = projects[0];
    const rate = activeRateCard?.entries?.[0] || { billingRateMinor: 0, hourlyRateMinor: 0 };
    
    setFormData({
      projectId: activeProject?._id || '',
      taskId: '',
      date: today,
      minutes: 60,
      description: '',
      activityTag: 'development',
      billable: true,
      billingRateMinor: rate.billingRateMinor || 0,
      costRateMinor: rate.hourlyRateMinor || 0,
    });
    setEditing(null);
    setCreating(true);
  }, [projects, activeRateCard]);

  const handleCreate = () => {
    resetForm();
  };

  const handleEdit = (item: any) => {
    setEditing(item);
    setFormData({
      projectId: item.projectId,
      taskId: item.taskId || '',
      date: item.date ? new Date(item.date).toISOString().split('T')[0] : '',
      minutes: item.minutes,
      description: item.description,
      activityTag: item.activityTag || 'development',
      billable: item.billable,
      billingRateMinor: item.billingRateMinor,
      costRateMinor: item.costRateMinor,
    });
    setCreating(true);
  };

  const handleView = (item: any) => {
    setViewing(item);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!formData.projectId) return toast.error('Project is required');
    if (!formData.description?.trim()) return toast.error('Description is required');
    if (!formData.minutes || formData.minutes <= 0) return toast.error('Valid minutes required');

    setSaving(true);
    try {
      const payload = {
        projectId: formData.projectId,
        taskId: formData.taskId || null,
        date: formData.date ? new Date(formData.date).toISOString() : null,
        minutes: Number(formData.minutes),
        description: formData.description,
        activityTag: formData.activityTag,
        billable: formData.billable,
        billingRateMinor: Number(formData.billingRateMinor) || 0,
        costRateMinor: Number(formData.costRateMinor) || 0,
      };

      // We'll use the API directly since we need POST to /api/v2/services-projects/timesheets
      const response = await fetch('/api/v2/services-projects/timesheets', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify(payload),
      });
      
      if (!response.ok) {
        const err = await response.json();
        throw new Error(err.error || 'Failed to save');
      }
      
      toast.success(editing ? 'Timesheet updated' : 'Timesheet created');
      resetForm();
    } catch (err) {
      toast.error(errorText(err, 'Failed to save timesheet'));
    } finally {
      setSaving(false);
    }
  };

  const handleTimerStart = () => {
    if (!timerProjectId) return toast.error('Select a project first');
    if (!timerDescription?.trim()) return toast.error('Add a description');
    
    setTimerRunning(true);
    setTimerPaused(false);
    setTimerSeconds(0);
  };

  const handleTimerPause = () => {
    setTimerPaused(true);
  };

  const handleTimerResume = () => {
    setTimerPaused(false);
  };

  const handleTimerStop = async () => {
    setTimerRunning(false);
    setTimerPaused(false);
    
    const minutes = Math.round(timerSeconds / 60);
    if (minutes < 1) return toast.error('Timer too short');
    
    const project = projects.find(p => p._id === timerProjectId);
    const rate = activeRateCard?.entries?.[0] || { billingRateMinor: 0, hourlyRateMinor: 0 };
    
    try {
      await fetch('/api/v2/services-projects/timesheets', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          projectId: timerProjectId,
          taskId: timerTaskId || null,
          date: localToday(),
          minutes,
          description: timerDescription,
          activityTag: 'development',
          billable: timerBillable,
          billingRateMinor: rate.billingRateMinor || 0,
          costRateMinor: rate.hourlyRateMinor || 0,
        }),
      });
      
      toast.success(`Saved ${formatDuration(minutes * 60)} to ${project?.name || 'project'}`);
      setTimerSeconds(0);
      setTimerDescription('');
      setTimerProjectId('');
      setTimerTaskId('');
    } catch (err) {
      toast.error(errorText(err, 'Failed to save timer entry'));
    }
  };

  const handleTimerDiscard = () => {
    if (!window.confirm('Discard current timer?')) return;
    setTimerRunning(false);
    setTimerPaused(false);
    setTimerSeconds(0);
    setTimerDescription('');
    setTimerProjectId('');
    setTimerTaskId('');
  };

  const handleAction = async (id: string, action: string, reason?: string) => {
    try {
      const response = await fetch(`/api/v2/services-projects/timesheets/${id}/${action}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ reason }),
      });
      
      if (!response.ok) {
        const err = await response.json();
        throw new Error(err.error || 'Failed');
      }
      
      toast.success(`Timesheet ${action}d`);
      refetchTimesheets();
    } catch (err) {
      toast.error(errorText(err, `Failed to ${action} timesheet`));
    }
  };

  const toggleSelected = (id: string) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  /**
   * Wires up the header/row checkboxes the Entries table already rendered
   * but never connected to anything. Each timesheet is approved individually
   * so one rejection-worthy entry doesn't block the rest of the batch.
   */
  const bulkApprove = async () => {
    if (!selectedIds.size) return;
    setBulkApproving(true);
    let ok = 0;
    const failures: string[] = [];
    for (const id of selectedIds) {
      try {
        const response = await fetch(`/api/v2/services-projects/timesheets/${id}/approve`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          credentials: 'include',
          body: JSON.stringify({}),
        });
        if (!response.ok) throw new Error((await response.json())?.error || 'Failed');
        ok += 1;
      } catch (err) {
        failures.push(errorText(err, 'failed'));
      }
    }
    setSelectedIds(new Set());
    setBulkApproving(false);
    refetchTimesheets();
    if (ok) toast.success(`Approved ${ok} timesheet${ok === 1 ? '' : 's'}`);
    if (failures.length) toast.error(`${failures.length} could not be approved. ${failures[0]}`);
  };

  const handleDelete = async (id: string) => {
    if (!window.confirm('Delete this timesheet entry?')) return;
    try {
      await fetch(`/api/v2/services-projects/timesheets/${id}`, {
        method: 'DELETE',
        credentials: 'include',
      });
      toast.success('Timesheet deleted');
    } catch (err) {
      toast.error(errorText(err, 'Failed to delete'));
    }
  };

  // Weekly view computation
  const weeklyData = useMemo(() => {
    const weeks: Record<string, { total: number; byProject: Record<string, number>; byUser: Record<string, number> }> = {};
    
    filteredTimesheets.forEach(t => {
      if (!t.date) return;
      const date = new Date(t.date);
      const weekStart = new Date(date);
      weekStart.setDate(date.getDate() - date.getDay());
      const weekKey = weekStart.toISOString().split('T')[0];
      
      if (!weeks[weekKey]) {
        weeks[weekKey] = { total: 0, byProject: {}, byUser: {} };
      }
      weeks[weekKey].total += t.minutes || 0;
      weeks[weekKey].byProject[t.projectId] = (weeks[weekKey].byProject[t.projectId] || 0) + (t.minutes || 0);
      weeks[weekKey].byUser[t.userId] = (weeks[weekKey].byUser[t.userId] || 0) + (t.minutes || 0);
    });
    
    return Object.entries(weeks)
      .map(([weekStart, data]) => ({
        weekStart,
        weekEnd: new Date(new Date(weekStart).getTime() + 6 * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
        totalMinutes: data.total,
        totalHours: (data.total / 60).toFixed(1),
        byProject: data.byProject,
        byUser: data.byUser,
      }))
      .sort((a, b) => b.weekStart.localeCompare(a.weekStart));
  }, [filteredTimesheets]);

  const isLoading = timesheetsLoading;
  const totalMinutes = filteredTimesheets.reduce((sum, t) => sum + (t.minutes || 0), 0);
  const billableMinutes = filteredTimesheets.filter(t => t.billable).reduce((sum, t) => sum + (t.minutes || 0), 0);
  const billableAmount = filteredTimesheets
    .filter(t => t.billable)
    .reduce((sum, t) => sum + Math.round((t.minutes || 0) / 60 * (t.billingRateMinor || 0)), 0);

  return (
    <div className="space-y-6 pb-16">
      {/* Header */}
      <PageHeader
        icon={CalendarClock}
        eyebrow="Projects & Services"
        title="Timesheets"
        description={<>Track time, manage approvals, and generate billing from approved entries</>}
        actions={
          <><div className="flex gap-2">
                  <Button variant="outline" onClick={() => setActiveTab('timer')}>
                    <Timer className="w-4 h-4 mr-2" /> Timer
                  </Button>
                  <Button variant="outline" onClick={() => setActiveTab('entries')}>
                    <FileText className="w-4 h-4 mr-2" /> Entries
                  </Button>
                  <Button variant="outline" onClick={() => setActiveTab('weekly')}>
                    <Calendar className="w-4 h-4 mr-2" /> Weekly
                  </Button>
                  <Button variant="outline" onClick={() => setActiveTab('approval')}>
                    <CheckCircle className="w-4 h-4 mr-2" /> Approval
                  </Button>
                  {activeTab !== 'timer' && (
                    <Button onClick={handleCreate}>
                      <Plus className="w-4 h-4 mr-2" /> Add Entry
                    </Button>
                  )}
                </div></>
        }
      />

      {/* Summary Cards */}
      <div className="grid gap-4 sm:grid-cols-4">
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-500">Total Hours</p>
                <p className="text-2xl font-bold">{(totalMinutes / 60).toFixed(1)}</p>
              </div>
              <Clock className="w-8 h-8 text-blue-500" />
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-500">Billable Hours</p>
                <p className="text-2xl font-bold text-emerald-600">{(billableMinutes / 60).toFixed(1)}</p>
              </div>
              <DollarSign className="w-8 h-8 text-emerald-500" />
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-500">Billable Value</p>
                <p className="text-2xl font-bold text-emerald-600">{formatINR(billableAmount)}</p>
              </div>
              <TrendingUp className="w-8 h-8 text-emerald-500" />
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-500">Entries</p>
                <p className="text-2xl font-bold">{filteredTimesheets.length}</p>
              </div>
              <FileText className="w-8 h-8 text-purple-500" />
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Timer Tab */}
      {activeTab === 'timer' && (
        <Card>
          <CardHeader>
            <CardTitle>Live Timer</CardTitle>
          </CardHeader>
          <CardContent className="space-y-6">
            <div className="text-center py-8">
              <div className="relative w-48 h-48 mx-auto">
                <svg className="w-full h-full transform -rotate-90">
                  <circle
                    cx="96" cy="96" r="88"
                    fill="none" stroke="currentColor" strokeWidth="8"
                    className="text-gray-200 dark:text-gray-700"
                  />
                  <circle
                    cx="96" cy="96" r="88"
                    fill="none" stroke="currentColor" strokeWidth="8"
                    strokeDasharray={553} strokeDashoffset={553}
                    strokeLinecap="round"
                    className="text-blue-600 transition-all duration-1000"
                    style={{ strokeDashoffset: 553 - (timerSeconds / 3600) * 553 }}
                  />
                </svg>
                <div className="absolute inset-0 flex items-center justify-center">
                  <span className="text-4xl font-mono font-bold text-gray-900 dark:text-white">
                    {String(Math.floor(timerSeconds / 3600)).padStart(2, '0')}:{String(Math.floor((timerSeconds % 3600) / 60)).padStart(2, '0')}:{String(timerSeconds % 60).padStart(2, '0')}
                  </span>
                </div>
              </div>
            </div>

            <div className="grid gap-4 sm:grid-cols-2">
              <div className="space-y-1">
                <label className="block text-sm font-medium">Project *</label>
                <Select
                  value={timerProjectId}
                  onChange={e => setTimerProjectId(e.target.value)}
                  options={[{ value: '', label: 'Select project' }, ...projects.map(p => ({ value: p._id, label: p.name }))]}
                  disabled={timerRunning}
                />
              </div>
              <div className="space-y-1">
                <label className="block text-sm font-medium">Task</label>
                <Select
                  value={timerTaskId}
                  onChange={e => setTimerTaskId(e.target.value)}
                  options={[{ value: '', label: 'No task' }, ...timerTasks.map((t: any) => ({ value: t._id || t.id, label: t.name || t.code || t.title || t._id }))]}
                  disabled={timerRunning}
                />
              </div>
            </div>

            <div className="space-y-1">
              <label className="block text-sm font-medium">Description *</label>
              <Input
                placeholder="What are you working on?"
                value={timerDescription}
                onChange={e => setTimerDescription(e.target.value)}
                disabled={timerRunning}
              />
            </div>

            <div className="flex items-center gap-4">
              <label className="flex items-center gap-2 text-sm">
                <input
                  type="checkbox"
                  checked={timerBillable}
                  onChange={e => setTimerBillable(e.target.checked)}
                  disabled={timerRunning}
                />
                Billable time
              </label>
            </div>

            <div className="flex gap-3 justify-center">
              {!timerRunning ? (
                <Button onClick={handleTimerStart} size="lg" className="min-w-[140px]">
                  <Play className="w-5 h-5 mr-2" /> Start Timer
                </Button>
              ) : timerPaused ? (
                <>
                  <Button variant="secondary" onClick={handleTimerResume} className="min-w-[140px]">
                    <Play className="w-5 h-5 mr-2" /> Resume
                  </Button>
                  <Button variant="outline" onClick={handleTimerDiscard}>
                    <Square className="w-5 h-5 mr-2" /> Discard
                  </Button>
                </>
              ) : (
                <>
                  <Button variant="secondary" onClick={handleTimerPause} className="min-w-[140px]">
                    <Pause className="w-5 h-5 mr-2" /> Pause
                  </Button>
                  <Button onClick={handleTimerStop} className="min-w-[140px] bg-emerald-600 hover:bg-emerald-700">
                    <Square className="w-5 h-5 mr-2" /> Stop & Save
                  </Button>
                </>
              )}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Entries Tab */}
      {activeTab === 'entries' && (
        <div className="space-y-4">
          {/* Filters */}
          <Card>
            <CardContent className="p-4">
              <div className="flex flex-wrap gap-4 items-end">
                <div className="flex-1 min-w-[200px]">
                  <label className="block text-xs font-medium text-gray-500 mb-1">Search</label>
                  <Input
                    placeholder="Search description, project, user..."
                    value={search}
                    onChange={e => setSearch(e.target.value)}
                    leftIcon={<Search className="w-4 h-4" />}
                  />
                </div>
                <div className="min-w-[150px]">
                  <label className="block text-xs font-medium text-gray-500 mb-1">Status</label>
                  <Select
                    value={statusFilter}
                    onChange={e => setStatusFilter(e.target.value)}
                    options={[{ value: '', label: 'All' }, ...TIMESHEET_STATUSES.map(s => ({ value: s.value, label: s.label }))]}
                  />
                </div>
                <div className="min-w-[150px]">
                  <label className="block text-xs font-medium text-gray-500 mb-1">Project</label>
                  <Select
                    value={projectFilter}
                    onChange={e => setProjectFilter(e.target.value)}
                    options={[{ value: '', label: 'All' }, ...projects.map(p => ({ value: p._id, label: p.name }))]}
                  />
                </div>
                <div className="min-w-[150px]">
                  <label className="block text-xs font-medium text-gray-500 mb-1">User</label>
                  <Select
                    value={userFilter}
                    onChange={e => setUserFilter(e.target.value)}
                    options={[{ value: '', label: 'All' }, ...users.map(u => ({ value: u.id, label: u.display_name || u.username || u.id }))]}
                  />
                </div>
                <div className="min-w-[130px]">
                  <label className="block text-xs font-medium text-gray-500 mb-1">From</label>
                  <Input type="date" value={dateFrom} onChange={e => setDateFrom(e.target.value)} />
                </div>
                <div className="min-w-[130px]">
                  <label className="block text-xs font-medium text-gray-500 mb-1">To</label>
                  <Input type="date" value={dateTo} onChange={e => setDateTo(e.target.value)} />
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Table */}
          {isLoading ? (
            <Card>
              <CardContent className="py-12 text-center">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto"></div>
              </CardContent>
            </Card>
          ) : filteredTimesheets.length === 0 ? (
            <Card>
              <CardContent className="py-12 text-center">
                <Clock className="w-12 h-12 text-gray-300 mx-auto mb-4" />
                <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-2">No timesheet entries</h3>
                <p className="text-gray-500 mb-4">Create your first entry or use the timer.</p>
                <Button onClick={() => setActiveTab('timer')}>
                  <Timer className="w-4 h-4 mr-2" /> Start Timer
                </Button>
              </CardContent>
            </Card>
          ) : (
            <Card>
              {(() => {
                const approvableIds = filteredTimesheets.filter((t: any) => t.status === 'submitted').map((t: any) => t._id);
                const allSelected = approvableIds.length > 0 && approvableIds.every((id) => selectedIds.has(id));
                return selectedIds.size > 0 || approvableIds.length > 0 ? (
                  <div className="flex flex-wrap items-center justify-between gap-2 px-4 py-2.5 border-b bg-slate-50 dark:bg-slate-800/40">
                    <label className="flex items-center gap-2 text-xs font-semibold text-gray-500">
                      <input
                        type="checkbox"
                        checked={allSelected}
                        onChange={(e) => setSelectedIds(e.target.checked ? new Set(approvableIds) : new Set())}
                        aria-label="Select all submitted entries"
                      />
                      {selectedIds.size > 0 ? `${selectedIds.size} selected` : 'Select all submitted'}
                    </label>
                    {selectedIds.size > 0 && (
                      <div className="flex gap-2">
                        <Button size="sm" onClick={bulkApprove} disabled={bulkApproving}>
                          <CheckCircle className="w-3.5 h-3.5 mr-1" /> Approve {selectedIds.size}
                        </Button>
                        <Button size="sm" variant="outline" onClick={() => setSelectedIds(new Set())}>Clear</Button>
                      </div>
                    )}
                  </div>
                ) : null;
              })()}
              <div className="overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead className="w-8"></TableHead>
                      <TableHead>Date</TableHead>
                      <TableHead>Project</TableHead>
                      <TableHead>User</TableHead>
                      <TableHead>Duration</TableHead>
                      <TableHead>Activity</TableHead>
                      <TableHead>Description</TableHead>
                      <TableHead>Rate</TableHead>
                      <TableHead>Amount</TableHead>
                      <TableHead>Status</TableHead>
                      <TableHead className="w-48">Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {filteredTimesheets.map((t: any) => {
                      const statusConfig = getStatusConfig(t.status);
                      const amount = Math.round((t.minutes || 0) / 60 * (t.billingRateMinor || 0));
                      return (
                        <TableRow key={t._id}>
                          <TableCell className="w-8">
                            {t.status === 'submitted' && (
                              <input
                                type="checkbox"
                                className="rounded border-gray-300"
                                checked={selectedIds.has(t._id)}
                                onChange={() => toggleSelected(t._id)}
                                aria-label={`Select entry for ${t.projectName || 'project'}`}
                              />
                            )}
                          </TableCell>
                          <TableCell>{t.date ? new Date(t.date).toLocaleDateString() : '—'}</TableCell>
                          <TableCell>{t.projectName || '—'}</TableCell>
                          <TableCell>{t.userName || t.userId || '—'}</TableCell>
                          <TableCell className="font-mono">{formatDuration(t.minutes || 0)}</TableCell>
                          <TableCell>
                            <Badge variant="secondary">{t.activityTag || '—'}</Badge>
                          </TableCell>
                          <TableCell className="max-w-xs truncate">{t.description || '—'}</TableCell>
                          <TableCell className="font-mono">{t.billable ? formatINR(t.billingRateMinor || 0) + '/hr' : 'Non-billable'}</TableCell>
                          <TableCell className="font-mono font-medium">{t.billable ? formatINR(amount) : '—'}</TableCell>
                          <TableCell>
                            <Badge className={statusConfig.color}>{statusConfig.label}</Badge>
                          </TableCell>
                          <TableCell className="w-48">
                            <div className="flex items-center gap-1">
                              <Button variant="ghost" size="icon" onClick={() => handleView(t)} title="View">
                                <Eye className="w-4 h-4" />
                              </Button>
                              {(t.status === 'draft' || t.status === 'rejected') && (
                                <Button variant="ghost" size="icon" onClick={() => handleEdit(t)} title="Edit">
                                  <Edit className="w-4 h-4" />
                                </Button>
                              )}
                              {t.status === 'draft' && (
                                <Button variant="ghost" size="icon" onClick={() => handleAction(t._id, 'submit')} title="Submit for approval">
                                  <AlertCircle className="w-4 h-4 text-blue-500" />
                                </Button>
                              )}
                              {t.status === 'submitted' && (
                                <>
                                  <Button variant="ghost" size="icon" onClick={() => handleAction(t._id, 'approve')} title="Approve" className="text-emerald-500">
                                    <CheckCircle className="w-4 h-4" />
                                  </Button>
                                  <Button variant="ghost" size="icon" onClick={() => {
                                    const reason = prompt('Rejection reason:');
                                    if (reason) handleAction(t._id, 'reject', reason);
                                  }} title="Reject" className="text-red-500">
                                    <XCircle className="w-4 h-4" />
                                  </Button>
                                </>
                              )}
                              {(t.status === 'draft' || t.status === 'rejected') && (
                                <Button variant="ghost" size="icon" onClick={() => handleDelete(t._id)} title="Delete" className="text-red-500">
                                  <Trash2 className="w-4 h-4" />
                                </Button>
                              )}
                            </div>
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </div>
            </Card>
          )}
        </div>
      )}

      {/* Weekly View Tab */}
      {activeTab === 'weekly' && (
        <Card>
          <CardHeader>
            <CardTitle>Weekly Summary</CardTitle>
          </CardHeader>
          <CardContent>
            {weeklyData.length === 0 ? (
              <div className="text-center py-12 text-gray-500">No data for selected filters</div>
            ) : (
              <div className="overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Week</TableHead>
                      <TableHead>Total Hours</TableHead>
                      <TableHead className="w-64">By Project</TableHead>
                      <TableHead className="w-64">By User</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {weeklyData.map((week) => (
                      <TableRow key={week.weekStart}>
                        <TableCell>
                          {new Date(week.weekStart).toLocaleDateString()} - {new Date(week.weekEnd).toLocaleDateString()}
                        </TableCell>
                        <TableCell className="font-mono font-medium">{week.totalHours}h</TableCell>
                        <TableCell>
                          <div className="text-sm">
                            {Object.entries(week.byProject).map(([pid, mins]) => {
                              const proj = projects.find(p => p._id === pid);
                              return <div key={pid} className="flex justify-between">{proj?.name || pid}: <span className="font-mono">{(mins/60).toFixed(1)}h</span></div>;
                            })}
                          </div>
                        </TableCell>
                        <TableCell>
                          <div className="text-sm">
                            {Object.entries(week.byUser).map(([uid, mins]) => {
                              const usr = users.find(u => u.id === uid);
                              return <div key={uid} className="flex justify-between">{usr?.display_name || usr?.username || uid}: <span className="font-mono">{(mins/60).toFixed(1)}h</span></div>;
                            })}
                          </div>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {/* Approval Tab */}
      {activeTab === 'approval' && (
        <Card>
          <CardHeader>
            <CardTitle>Pending Approvals</CardTitle>
          </CardHeader>
          <CardContent>
            {(() => {
              const pending = filteredTimesheets.filter(t => t.status === 'submitted');
              return pending.length === 0 ? (
                <div className="text-center py-12 text-gray-500">
                  <CheckCircle className="w-12 h-12 text-emerald-500 mx-auto mb-4" />
                  <h3 className="text-lg font-medium mb-2">All caught up!</h3>
                  <p>No timesheets pending approval.</p>
                </div>
              ) : (
                <div className="overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Date</TableHead>
                      <TableHead>Project</TableHead>
                      <TableHead>User</TableHead>
                      <TableHead>Duration</TableHead>
                      <TableHead>Description</TableHead>
                      <TableHead>Amount</TableHead>
                      <TableHead>Submitted</TableHead>
                      <TableHead className="w-48">Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {pending.map((t: any) => (
                      <TableRow key={t._id}>
                        <TableCell>{t.date ? new Date(t.date).toLocaleDateString() : '—'}</TableCell>
                        <TableCell>{t.projectName || '—'}</TableCell>
                        <TableCell>{t.userName || t.userId || '—'}</TableCell>
                        <TableCell className="font-mono">{formatDuration(t.minutes || 0)}</TableCell>
                        <TableCell className="max-w-xs truncate">{t.description || '—'}</TableCell>
                        <TableCell className="font-mono">{formatINR(Math.round((t.minutes || 0) / 60 * (t.billingRateMinor || 0)))}</TableCell>
                        <TableCell>{t.submittedAt ? new Date(t.submittedAt).toLocaleString() : '—'}</TableCell>
                        <TableCell className="w-48">
                          <div className="flex gap-1">
                            <Button variant="secondary" size="sm" onClick={() => handleAction(t._id, 'approve')}>
                              <CheckCircle className="w-4 h-4 mr-1" /> Approve
                            </Button>
                            <Button variant="outline" size="sm" onClick={() => {
                              const reason = prompt('Rejection reason:');
                              if (reason) handleAction(t._id, 'reject', reason);
                            }}>
                              <XCircle className="w-4 h-4 mr-1" /> Reject
                            </Button>
                          </div>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            )})()}
          </CardContent>
        </Card>
      )}

      {/* Create/Edit Modal */}
      <Modal open={creating} onClose={() => setCreating(false)} title={editing ? 'Edit Timesheet Entry' : 'New Timesheet Entry'} size="lg">
        <form onSubmit={handleSubmit} className="space-y-5 p-6">
          <div className="grid gap-4 sm:grid-cols-2">
            <div className="space-y-1">
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">Project *</label>
              <Select
                value={formData.projectId}
                onChange={e => setFormData({ ...formData, projectId: e.target.value })}
                options={projects.map((p: any) => ({ value: p._id || p.id, label: p.name }))}
              />
            </div>
            <div className="space-y-1">
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">Task</label>
              <Select
                value={formData.taskId}
                onChange={e => setFormData({ ...formData, taskId: e.target.value })}
                options={[{ value: '', label: 'No task' }, ...formTasks.map((t: any) => ({ value: t._id || t.id, label: t.name || t.code || t.title || t._id }))]}
              />
            </div>
          </div>

          <div className="grid gap-4 sm:grid-cols-3">
            <div className="space-y-1">
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">Date *</label>
              <Input
                type="date"
                value={formData.date}
                onChange={e => setFormData({ ...formData, date: e.target.value })}
              />
            </div>
            <div className="space-y-1">
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">Minutes *</label>
              <Input
                type="number"
                min="1"
                max="1440"
                placeholder="60"
                value={formData.minutes}
                onChange={e => setFormData({ ...formData, minutes: Number(e.target.value) || 0 })}
              />
            </div>
            <div className="space-y-1">
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">Activity</label>
              <Select
                value={formData.activityTag}
                onChange={e => setFormData({ ...formData, activityTag: e.target.value })}
                options={ACTIVITY_TAGS.map(a => ({ value: a, label: a.replace('_', ' ') }))}
              />
            </div>
          </div>

          <div className="space-y-1">
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">Description *</label>
            <Input
              placeholder="Describe the work done..."
              value={formData.description}
              onChange={e => setFormData({ ...formData, description: e.target.value })}
            />
          </div>

          <div className="grid gap-4 sm:grid-cols-4">
            <div className="space-y-1">
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">Billing Rate/hr</label>
              <Input
                type="number"
                min="0"
                step="100"
                placeholder="0"
                value={formData.billingRateMinor}
                onChange={e => setFormData({ ...formData, billingRateMinor: Number(e.target.value) || 0 })}
              />
            </div>
            <div className="space-y-1">
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">Cost Rate/hr</label>
              <Input
                type="number"
                min="0"
                step="100"
                placeholder="0"
                value={formData.costRateMinor}
                onChange={e => setFormData({ ...formData, costRateMinor: Number(e.target.value) || 0 })}
              />
            </div>
            <div className="space-y-1">
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">Est. Amount</label>
              <Input
                type="text"
                readOnly
                value={formatINR(Math.round((formData.minutes || 0) / 60 * (formData.billingRateMinor || 0)))}
                className="bg-gray-50 dark:bg-gray-800"
              />
            </div>
            <div className="flex items-end">
              <label className="flex items-center gap-2 text-sm font-medium">
                <input
                  type="checkbox"
                  checked={formData.billable}
                  onChange={e => setFormData({ ...formData, billable: e.target.checked })}
                />
                Billable
              </label>
            </div>
          </div>

          <div className="flex justify-end gap-3 border-t pt-4 mt-4">
            <Button type="button" variant="outline" onClick={() => { setCreating(false); setEditing(null); }}>
              Cancel
            </Button>
            <Button type="submit" loading={saving}>
              {editing ? 'Update' : 'Create'} Entry
            </Button>
          </div>
        </form>
      </Modal>

      {/* View Modal */}
      <Modal open={!!viewing} onClose={() => setViewing(null)} title="Timesheet Details" size="lg">
        {viewing && (
          <div className="p-6 space-y-4">
            <div className="grid gap-4 sm:grid-cols-2">
              <div><span className="text-sm text-gray-500">Project</span><p className="font-medium">{viewing.projectName || '—'}</p></div>
              <div><span className="text-sm text-gray-500">User</span><p>{viewing.userName || viewing.userId || '—'}</p></div>
              <div><span className="text-sm text-gray-500">Date</span><p>{viewing.date ? new Date(viewing.date).toLocaleDateString() : '—'}</p></div>
              <div><span className="text-sm text-gray-500">Duration</span><p className="font-mono">{formatDuration(viewing.minutes || 0)}</p></div>
              <div><span className="text-sm text-gray-500">Activity</span><p><Badge variant="secondary">{viewing.activityTag || '—'}</Badge></p></div>
              <div><span className="text-sm text-gray-500">Billable</span><p>{viewing.billable ? 'Yes' : 'No'}</p></div>
              <div><span className="text-sm text-gray-500">Billing Rate</span><p className="font-mono">{formatINR(viewing.billingRateMinor || 0)}/hr</p></div>
              <div><span className="text-sm text-gray-500">Cost Rate</span><p className="font-mono">{formatINR(viewing.costRateMinor || 0)}/hr</p></div>
              <div><span className="text-sm text-gray-500">Amount</span><p className="font-bold text-emerald-600">{formatINR(Math.round((viewing.minutes || 0) / 60 * (viewing.billingRateMinor || 0)))}</p></div>
              <div><span className="text-sm text-gray-500">Status</span><p><Badge className={getStatusConfig(viewing.status).color}>{getStatusConfig(viewing.status).label}</Badge></p></div>
            </div>
            <div className="border-t pt-4">
              <span className="text-sm text-gray-500">Description</span>
              <p className="mt-1 whitespace-pre-wrap">{viewing.description || '—'}</p>
            </div>
          </div>
        )}
      </Modal>
    </div>
  );
}